import React, { memo, useState, useMemo } from "react";
import {
  Box,
  Container,
  Grid2,
  Typography,
  Card,
  CardActionArea,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search, LocationOn, ArrowForward } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useGetAllStateListQuery } from "../../Services/auto-complete.services";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";

// All 50 US States with their abbreviations
const ALL_US_STATES = [
  { state: "AL", stateName: "Alabama", stateID: "AL" },
  { state: "AK", stateName: "Alaska", stateID: "AK" },
  { state: "AZ", stateName: "Arizona", stateID: "AZ" },
  { state: "AR", stateName: "Arkansas", stateID: "AR" },
  { state: "CA", stateName: "California", stateID: "CA" },
  { state: "CO", stateName: "Colorado", stateID: "CO" },
  { state: "CT", stateName: "Connecticut", stateID: "CT" },
  { state: "DE", stateName: "Delaware", stateID: "DE" },
  { state: "FL", stateName: "Florida", stateID: "FL" },
  { state: "GA", stateName: "Georgia", stateID: "GA" },
  { state: "HI", stateName: "Hawaii", stateID: "HI" },
  { state: "ID", stateName: "Idaho", stateID: "ID" },
  { state: "IL", stateName: "Illinois", stateID: "IL" },
  { state: "IN", stateName: "Indiana", stateID: "IN" },
  { state: "IA", stateName: "Iowa", stateID: "IA" },
  { state: "KS", stateName: "Kansas", stateID: "KS" },
  { state: "KY", stateName: "Kentucky", stateID: "KY" },
  { state: "LA", stateName: "Louisiana", stateID: "LA" },
  { state: "ME", stateName: "Maine", stateID: "ME" },
  { state: "MD", stateName: "Maryland", stateID: "MD" },
  { state: "MA", stateName: "Massachusetts", stateID: "MA" },
  { state: "MI", stateName: "Michigan", stateID: "MI" },
  { state: "MN", stateName: "Minnesota", stateID: "MN" },
  { state: "MS", stateName: "Mississippi", stateID: "MS" },
  { state: "MO", stateName: "Missouri", stateID: "MO" },
  { state: "MT", stateName: "Montana", stateID: "MT" },
  { state: "NE", stateName: "Nebraska", stateID: "NE" },
  { state: "NV", stateName: "Nevada", stateID: "NV" },
  { state: "NH", stateName: "New Hampshire", stateID: "NH" },
  { state: "NJ", stateName: "New Jersey", stateID: "NJ" },
  { state: "NM", stateName: "New Mexico", stateID: "NM" },
  { state: "NY", stateName: "New York", stateID: "NY" },
  { state: "NC", stateName: "North Carolina", stateID: "NC" },
  { state: "ND", stateName: "North Dakota", stateID: "ND" },
  { state: "OH", stateName: "Ohio", stateID: "OH" },
  { state: "OK", stateName: "Oklahoma", stateID: "OK" },
  { state: "OR", stateName: "Oregon", stateID: "OR" },
  { state: "PA", stateName: "Pennsylvania", stateID: "PA" },
  { state: "RI", stateName: "Rhode Island", stateID: "RI" },
  { state: "SC", stateName: "South Carolina", stateID: "SC" },
  { state: "SD", stateName: "South Dakota", stateID: "SD" },
  { state: "TN", stateName: "Tennessee", stateID: "TN" },
  { state: "TX", stateName: "Texas", stateID: "TX" },
  { state: "UT", stateName: "Utah", stateID: "UT" },
  { state: "VT", stateName: "Vermont", stateID: "VT" },
  { state: "VA", stateName: "Virginia", stateID: "VA" },
  { state: "WA", stateName: "Washington", stateID: "WA" },
  { state: "WV", stateName: "West Virginia", stateID: "WV" },
  { state: "WI", stateName: "Wisconsin", stateID: "WI" },
  { state: "WY", stateName: "Wyoming", stateID: "WY" },
];

// State images mapping - using placeholder gradients for visual appeal
const getStateGradient = (index: number) => {
  const gradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
    "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
    "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
  ];
  return gradients[index % gradients.length];
};

const StateCard = memo(({ state, index }: { state: any; index: number }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/${state.stateID}/state/${state?.state}`);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        border: "1px solid #e0e0e0",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          "& .state-arrow": {
            transform: "translateX(4px)",
            opacity: 1,
          },
        },
      }}
    >
      <CardActionArea onClick={handleClick}>
        <Box
          sx={{
            background: getStateGradient(index),
            height: { xs: 100, sm: 120 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontWeight: 800,
              letterSpacing: 2,
            }}
          >
            {state.state}
          </Typography>
        </Box>
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationOn
                sx={{ fontSize: 18, color: "primary.main", opacity: 0.8 }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {state.stateName}
              </Typography>
            </Box>
            <ArrowForward
              className="state-arrow"
              sx={{
                fontSize: 18,
                color: "primary.main",
                opacity: 0.5,
                transition: "all 0.3s ease",
              }}
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: "0.85rem" }}
          >
            Browse real estate
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
});

const StateCardSkeleton = () => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "16px",
      overflow: "hidden",
      border: "1px solid #e0e0e0",
    }}
  >
    <Skeleton variant="rectangular" height={120} animation="wave" />
    <Box sx={{ p: 2.5 }}>
      <Skeleton variant="text" width="70%" height={24} animation="wave" />
      <Skeleton variant="text" width="50%" height={20} animation="wave" />
    </Box>
  </Card>
);

const StateList = memo(() => {
  const { isLoading } = useGetAllStateListQuery("");
  const [searchQuery, setSearchQuery] = useState("");

  // Use all 50 US states
  const states = ALL_US_STATES;

  // Filter states based on search query
  const filteredStates = useMemo(() => {
    if (!searchQuery) return states;
    return states.filter(
      (state) =>
        state.stateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        state.state.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [states, searchQuery]);

  return (
    <Box sx={{mt: 4}}>
      <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh" }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)",
            py: { xs: 6, md: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              top: -100,
              right: -50,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              bottom: -50,
              left: "20%",
            }}
          />

          <Container maxWidth="xl">
            <FullPaddingWrapper>
              <Box textAlign="center" position="relative" zIndex={1}>
                <Typography
                  variant="h2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  }}
                >
                  Browse by State
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontWeight: 400,
                    mb: 4,
                    maxWidth: 600,
                    mx: "auto",
                    fontSize: { xs: "1rem", md: "1.1rem" },
                  }}
                >
                  Explore real estate listings across all 50 states. Find your
                  dream home anywhere in the United States.
                </Typography>

                {/* Search Box */}
                <Box sx={{ maxWidth: 500, mx: "auto" }}>
                  <TextField
                    fullWidth
                    placeholder="Search for a state..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "12px",
                        "& fieldset": {
                          borderColor: "transparent",
                        },
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </FullPaddingWrapper>
          </Container>
        </Box>

        {/* States Grid */}
        <Container maxWidth="xl">
          <FullPaddingWrapper>
            <Box py={{ xs: 4, md: 8 }}>
              {/* Results count */}
              {!isLoading && (
                <Box mb={4}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery
                      ? `${filteredStates.length} states found`
                      : `${states.length} states available`}
                  </Typography>
                </Box>
              )}

              <Grid2 container spacing={{ xs: 2, sm: 3 }}>
                {isLoading
                  ? Array.from(new Array(12)).map((_, index) => (
                      <Grid2
                        key={index}
                        size={{
                          xs: 6,
                          sm: 4,
                          md: 3,
                          lg: 12 / 5,
                        }}
                      >
                        <StateCardSkeleton />
                      </Grid2>
                    ))
                  : filteredStates.map((state: any, index: number) => (
                      <Grid2
                        key={state.stateID || index}
                        size={{
                          xs: 6,
                          sm: 4,
                          md: 3,
                          lg: 12 / 5,
                        }}
                      >
                        <StateCard state={state} index={index} />
                      </Grid2>
                    ))}
              </Grid2>

              {/* No results message */}
              {!isLoading && filteredStates.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 10,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No states found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search query
                  </Typography>
                </Box>
              )}
            </Box>
          </FullPaddingWrapper>
        </Container>
      </Box>
    </Box>
  );
});

export default StateList;
